import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useCart } from '@/context/CartContext';
import { ArrowLeft, Check, ChevronLeft, ChevronRight, Star, Truck, Shield, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import ReviewSection from '@/components/ReviewSection';
import { getApiUrl, getImageUrl, getBackendUrl } from '@/utils/api';

const API = getApiUrl();

// Star Rating Component
const StarRating = ({ rating, size = 'md' }) => {
  const sizeClasses = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5'
  };
  
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`${sizeClasses[size]} ${
            star <= Math.round(rating)
              ? 'fill-primary text-primary'
              : 'fill-zinc-700 text-zinc-700'
          }`}
        />
      ))}
    </div>
  );
};

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, loading: cartLoading } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState('');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [reviewStats, setReviewStats] = useState({ average_rating: 0, total_reviews: 0 });

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API}/products/${id}`);
        setProduct(response.data);
        setSelectedSize(response.data.sizes[0]);
        
        // Fetch review stats
        try {
          const statsResponse = await axios.get(`${API}/reviews/stats/${id}`);
          setReviewStats(statsResponse.data);
        } catch (e) {
          console.log('No reviews yet');
        }
      } catch (error) {
        console.error('Error fetching product:', error);
        toast.error('Erro ao carregar produto');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  const handleAddToCart = async () => {
    if (!selectedSize) {
      toast.error('Selecione um tamanho');
      return;
    }

    try {
      await addToCart(product.id, selectedSize);
      toast.success('Produto adicionado ao carrinho!');
    } catch (error) {
      toast.error('Erro ao adicionar produto');
    }
  };

  // Get all images (use images array or fallback to single image)
  const getImages = () => {
    let rawImages = [];
    if (product?.images && product.images.length > 0) {
      rawImages = product.images;
    } else if (product?.image) {
      rawImages = [product.image];
    }
    // Convert all to full URLs and filter out broken ones
    return rawImages
      .map(img => getImageUrl(img))
      .filter(img => img && !img.includes('tshirt-ecommerce.emergent.host'));
  };

  const nextImage = () => {
    const images = getImages();
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    const images = getImages();
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="loading-indicator">
        <div className="h-16 w-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="product-not-found">
        <p className="text-zinc-400">Produto não encontrado</p>
      </div>
    );
  }

  const images = getImages();

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="product-detail-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-8"
          data-testid="back-button"
        >
          <ArrowLeft className="h-5 w-5" />
          <span className="uppercase tracking-wider text-sm">Voltar</span>
        </button>

        {/* Product Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Product Image Gallery */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="relative"
            data-testid="product-image-container"
          >
            {/* Main Image */}
            <div className="relative aspect-[3/4] border border-white/10 overflow-hidden mb-4">
              <img
                src={images[currentImageIndex]}
                alt={`${product.name} - Imagem ${currentImageIndex + 1}`}
                className="w-full h-full object-cover"
                data-testid="product-image"
              />
              {product.featured && (
                <div className="absolute top-4 left-4 bg-primary text-black px-4 py-2 font-bold uppercase tracking-wider text-sm" data-testid="featured-badge">
                  Destaque
                </div>
              )}
              
              {/* Navigation Arrows - Only show if more than 1 image */}
              {images.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/70 hover:bg-black text-white p-2 transition-colors"
                    data-testid="prev-image-button"
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/70 hover:bg-black text-white p-2 transition-colors"
                    data-testid="next-image-button"
                  >
                    <ChevronRight className="h-6 w-6" />
                  </button>
                  
                  {/* Image Counter */}
                  <div className="absolute bottom-4 right-4 bg-black/70 text-white px-3 py-1 text-sm">
                    {currentImageIndex + 1} / {images.length}
                  </div>
                </>
              )}
            </div>
            
            {/* Thumbnail Gallery - Only show if more than 1 image */}
            {images.length > 1 && (
              <div className="grid grid-cols-5 gap-2">
                {images.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`aspect-square border-2 overflow-hidden transition-all ${
                      index === currentImageIndex
                        ? 'border-primary'
                        : 'border-white/10 hover:border-white/30'
                    }`}
                    data-testid={`thumbnail-${index}`}
                  >
                    <img
                      src={img}
                      alt={`${product.name} - Miniatura ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </motion.div>

          {/* Product Info */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex flex-col"
            data-testid="product-info"
          >
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-2" data-testid="product-name">
              {product.name}
            </h1>
            
            {/* Rating Display */}
            <div className="flex items-center gap-3 mb-4">
              <StarRating rating={reviewStats.average_rating} size="lg" />
              <span className="text-zinc-400 text-sm">
                {reviewStats.average_rating > 0 ? (
                  <>
                    {reviewStats.average_rating.toFixed(1)} ({reviewStats.total_reviews} {reviewStats.total_reviews === 1 ? 'avaliação' : 'avaliações'})
                  </>
                ) : (
                  'Seja o primeiro a avaliar!'
                )}
              </span>
            </div>
            
            <p className="font-mono text-3xl text-primary font-bold mb-6" data-testid="product-price">
              R$ {product.price.toFixed(2)}
              <span className="text-zinc-500 text-lg ml-2 line-through">R$ {(product.price * 1.3).toFixed(2)}</span>
            </p>

            {/* Description Box */}
            <div className="bg-zinc-900/50 border border-white/10 p-6 mb-8 rounded-lg" data-testid="product-description">
              <h3 className="font-heading text-lg font-bold text-white mb-3">Sobre este produto</h3>
              <p className="text-zinc-300 leading-relaxed">
                {product.description}
              </p>
              <div className="mt-4 pt-4 border-t border-white/10">
                <p className="text-sm text-zinc-400">
                  <span className="text-primary font-bold">✓</span> Peça exclusiva da coleção Don't Disturb
                </p>
              </div>
            </div>

            {/* Size Selection */}
            <div className="mb-6">
              <label className="block text-sm uppercase tracking-wider text-zinc-400 mb-3">
                Selecione o tamanho:
              </label>
              <div className="flex flex-wrap gap-3" data-testid="size-selector">
                {product.sizes.map(size => (
                  <button
                    key={size}
                    onClick={() => setSelectedSize(size)}
                    className={`w-16 h-16 border transition-all ${
                      selectedSize === size
                        ? 'border-primary bg-primary text-black font-bold'
                        : 'border-white/20 text-white hover:border-white'
                    }`}
                    data-testid={`size-option-${size}`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>

            {/* Add to Cart Button */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleAddToCart}
              disabled={cartLoading}
              className="w-full bg-primary text-black font-bold uppercase tracking-wider py-4 text-lg hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mb-6"
              data-testid="add-to-cart-button"
            >
              {cartLoading ? (
                <div className="h-6 w-6 border-2 border-black border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Check className="h-5 w-5" />
                  Adicionar ao Carrinho
                </>
              )}
            </motion.button>

            {/* Trust Badges */}
            <div className="grid grid-cols-3 gap-4 mb-8">
              <div className="flex flex-col items-center text-center p-3 bg-zinc-900/30 border border-white/5 rounded">
                <Truck className="h-6 w-6 text-primary mb-2" />
                <span className="text-xs text-zinc-400">Frete Grátis acima de R$199</span>
              </div>
              <div className="flex flex-col items-center text-center p-3 bg-zinc-900/30 border border-white/5 rounded">
                <Shield className="h-6 w-6 text-primary mb-2" />
                <span className="text-xs text-zinc-400">Compra 100% Segura</span>
              </div>
              <div className="flex flex-col items-center text-center p-3 bg-zinc-900/30 border border-white/5 rounded">
                <RefreshCw className="h-6 w-6 text-primary mb-2" />
                <span className="text-xs text-zinc-400">Troca Garantida</span>
              </div>
            </div>

            {/* Product Details */}
            <div className="pt-6 border-t border-white/10">
              <h3 className="font-heading text-xl font-bold text-white mb-4">Detalhes do Produto</h3>
              <ul className="space-y-2 text-zinc-300">
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  100% algodão premium
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  Estampa de alta qualidade que não desbota
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  Design exclusivo Don't Disturb
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  Modelagem oversized streetwear
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  Tecido macio e confortável
                </li>
              </ul>
            </div>
          </motion.div>
        </div>

        {/* Size Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-12 lg:mt-16"
          data-testid="size-chart-section"
        >
          <div className="border border-white/10 overflow-hidden">
            <img
              src="/images/tabela-medidas.png"
              alt="Tabela de Medidas - Don't Disturb Oversized"
              className="w-full h-auto"
              data-testid="size-chart-image"
            />
          </div>
        </motion.div>

        {/* Review Section */}
        <ReviewSection productId={product.id} />
      </div>
    </div>
  );
};

export default ProductDetail;